import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import {EventsChangeCoverGA4Type} from '@Libraries/ga-events';

export interface EventWizardImageVideoProps {
  variant?: EventWizardImageVideoVariant;
}

/**
 * How the cover photo control is laid out. Both variants open the same sources and editor
 * (useEventWizardImageVideoSources); only the layout differs.
 */
export enum EventWizardImageVideoVariant {
  /** Step 3: the source cards, or the cover with Edit design, Replace and Remove beside it. */
  SECTION = 'section',
  /** Step 1: a compact tile beside Styles that opens the sources in a menu (a bottom sheet on phones). */
  TILE = 'tile',
}

/** What opened step 1's cover menu: the cover tile, or a click on the cover in the preview. */
export enum EventWizardCoverMenuOpener {
  TILE = 'tile',
  PREVIEW = 'preview',
}

export interface EventWizardImageVideoSources {
  /** The source whose picker is opening, so its control can show a loader until the picker is up. */
  loadingMediaType: MediaTypes | null;
  /** True when the cover is a design the embedded editor can open: a template or one of the user's designs. */
  canEditCoverDesign: boolean;
  openMediaSource: (mediaType: MediaTypes) => void;
  /** Reopens the source the cover came from, or the one last picked. */
  replaceCoverPhoto: () => void;
  openCoverDesignEditor: () => void;
  removeCoverPhoto: () => void;
}

export interface CoverPhoto {
  uploadedUrl?: string;
  uploadMediaHID?: string;
  mediaType?: MediaTypes;
  selectedTemplateHID?: string;
  isPreviewLoading?: boolean;
  /**
   * The hashed filename of the event preset image the cover is, set only while it is that image: every other way of
   * picking a cover clears it. On save the server turns the image into the user's own upload.
   */
  eventPresetImageHash: string | null;
}

export enum MediaTypes {
  TEMPLATES = 0,
  MY_DESIGNS = 1,
  UPLOAD = 2,
}

export const MEDIA_TYPE_COVER_GA4_TYPES: Record<MediaTypes, EventsChangeCoverGA4Type> = {
  [MediaTypes.TEMPLATES]: EventsChangeCoverGA4Type.AI_DESIGNS,
  [MediaTypes.MY_DESIGNS]: EventsChangeCoverGA4Type.MY_DESIGNS,
  [MediaTypes.UPLOAD]: EventsChangeCoverGA4Type.UPLOAD,
};

export interface EventsPrefillMorphData {
  eventName: string;
  eventTimings: string;
  eventDuration: string;
  eventLocation: string;
  eventDates: string;
  eventType: string;
}

export interface CoverPhotoPickerLoading {
  loadingMediaType: MediaTypes | null;
  startLoading: (mediaType: MediaTypes) => void;
  stopLoading: VoidFunction;
}

/** core.js adds this to the body while any legacy jQuery dialog is open, such as the AI designs gallery. */
export const LEGACY_DIALOG_OPEN_BODY_CLASS = '-open-modal';

export const OPEN_MODAL_IDS_SEPARATOR = ',';

export interface EventWizardEmbeddedEditorOptions {
  hid: string;
  uploadedMediaType: MediaTypes;
  opts?: EventsPrefillMorphData;
  shouldUpsellAIPrefill?: boolean;
  /** Called once the editor starts opening; the first open loads the editor script before it does. */
  onOpened?: VoidFunction;
}

export interface EventTypeAiSearchParams {
  eventName: string;
  eventTypeHashedId: string;
}

export type EventsPreFillTextChanges = Partial<TextItemObject>;

export interface EventsPrefillMorphResponse {
  textContentChanges: Record<string, EventsPreFillTextChanges>;
}
